import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import Select from "../ui/Select";
import DatePicker from "../ui/DatePicker";
import {
  listRequestLocations,
  updateCandidateRequestDate,
  updateCandidateRequestLocation,
} from "../../api/candidateRequests";
import { pushToast } from "../ui/toastBus";

function UpdateModal({
  open,
  onClose,
  title,
  onSave,
  saving,
  disabled,
  children,
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSave} loading={saving} disabled={disabled}>
            Save
          </Button>
        </>
      }
    >
      {children}
    </Modal>
  );
}

// Applies `update(id)` to every request one after another, like the bulk approve/reject bar: a
// failure on one doesn't stop the rest.
const useUpdate = (requests, update, onClose, onDone, noun) =>
  useMutation({
    mutationFn: async () => {
      let failed = 0;
      let firstError = "";
      for (const r of requests) {
        try {
          await update(r.id);
        } catch (err) {
          failed += 1;
          firstError =
            firstError ||
            err.response?.data?.errors?.scheduled_date?.[0] ||
            err.response?.data?.message ||
            "";
        }
      }
      return { ok: requests.length - failed, failed, firstError };
    },
    onSuccess: ({ ok, failed, firstError }) => {
      pushToast(
        failed
          ? {
              type: "error",
              message: `${noun} updated for ${ok}, ${failed} failed${firstError ? ` — ${firstError}` : ""}.`,
            }
          : {
              type: "success",
              message: `${noun} updated${requests.length > 1 ? ` for ${ok} requests` : ""}.`,
            },
      );
      onDone();
      onClose();
    },
  });

const who = (requests) =>
  requests.length === 1
    ? requests[0].candidate.full_name
    : `${requests.length} approved requests selected`;

// `requests` is the list to update (one for a row button, many for a multi-select); null = closed.
export function UpdateDateModal({ type, requests, onClose, onDone }) {
  const list = useMemo(() => requests || [], [requests]);
  const [date, setDate] = useState("");
  useEffect(
    () => setDate(list.length === 1 ? list[0].scheduled_date || "" : ""),
    [list],
  );
  const mutation = useUpdate(
    list,
    (id) => updateCandidateRequestDate(type, id, date),
    onClose,
    onDone,
    "Date",
  );

  return (
    <UpdateModal
      open={!!requests}
      onClose={onClose}
      title="Update date"
      onSave={() => mutation.mutate()}
      saving={mutation.isPending}
      disabled={!date}
    >
      <p className="mb-3 text-sm text-gray-600">{requests && who(list)}</p>
      <DatePicker
        label="Date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
      />
    </UpdateModal>
  );
}

export function UpdateLocationModal({ type, requests, onClose, onDone }) {
  const list = useMemo(() => requests || [], [requests]);
  const [locationId, setLocationId] = useState("");
  useEffect(
    () =>
      setLocationId(
        list.length === 1 && list[0].location
          ? String(list[0].location.id)
          : "",
      ),
    [list],
  );
  const locationsQuery = useQuery({
    queryKey: ["request-locations"],
    queryFn: () => listRequestLocations(),
    enabled: !!requests,
  });
  const locations = locationsQuery.data || [];
  const mutation = useUpdate(
    list,
    (id) => updateCandidateRequestLocation(type, id, Number(locationId)),
    onClose,
    onDone,
    "Location",
  );

  return (
    <UpdateModal
      open={!!requests}
      onClose={onClose}
      title="Update location"
      onSave={() => mutation.mutate()}
      saving={mutation.isPending}
      disabled={!locationId}
    >
      <p className="mb-3 text-sm text-gray-600">{requests && who(list)}</p>
      <Select
        label="Location"
        value={locationId}
        onChange={(e) => setLocationId(e.target.value)}
      >
        <option value="">Select a location…</option>
        {locations.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
          </option>
        ))}
      </Select>
      {!locationsQuery.isLoading && locations.length === 0 && (
        <p className="mt-2 text-xs text-gray-500">
          No locations yet — add them under Masters → Locations.
        </p>
      )}
    </UpdateModal>
  );
}
